import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  Loader2,
  Check,
  MapPin,
} from "lucide-react";
import { SITE, SERVICES, CLIENTS } from "../site.config";
import WhatsAppButton from "./WhatsAppButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  ...SERVICES.map((s) => s.name),
  "Other / Not sure",
];

const PROPERTY_OPTIONS = [...CLIENTS, "Other"];

export default function QuoteForm() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", postcode: "",
    property_type: "", service: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildMailto = (p) => {
    const lines = [
      "═══════════════════════════════════════",
      "         NEW QUOTE REQUEST",
      "  Carpet & Lino Fitted by A.Moffat",
      "═══════════════════════════════════════",
      "",
      `  Name           :  ${p.name}`,
      `  Phone          :  ${p.phone}`,
      `  Email          :  ${p.email || "—"}`,
      `  Postcode       :  ${p.postcode || "—"}`,
      `  Property type  :  ${p.property_type || "—"}`,
      `  Service        :  ${p.service || "—"}`,
      "",
      "───────────────────────────────────────",
      "  Message",
      "───────────────────────────────────────",
      `  ${p.message ? p.message.replace(/\n/g, "\n  ") : "(no message provided)"}`,
      "",
      "───────────────────────────────────────",
      "  Sent via floorsbyamoffat.co.uk",
      "───────────────────────────────────────",
    ];
    const subject = `Quote Request — ${p.name}${p.service ? ` (${p.service})` : ""}`;
    const body = lines.join("\n");
    return `mailto:${SITE.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setSubmitting(true);
    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim() || null,
      postcode: form.postcode.trim() || null,
      property_type: form.property_type || null,
      service: form.service || null,
      message: form.message.trim() || null,
    };

    // Save a copy to the backend (best-effort — don't block the user if it fails)
    try {
      await axios.post(`${API}/quote`, payload);
    } catch (err) {
      console.warn("Backend save failed (continuing with mailto):", err);
    }

    // Open the user's own email client with the request pre-filled
    try {
      const url = buildMailto(payload);
      window.location.href = url;
      setSuccess(true);
      toast.success("Opening your email — just press send to finish.");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't open your email app. Please WhatsApp or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="section-pad bg-moffat-paper"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="text-center max-w-2xl mx-auto mb-8 md:mb-10">
          <p className="font-script text-3xl text-moffat-red leading-none">
            Get in touch
          </p>
          <h2 className="font-display text-4xl sm:text-5xl text-moffat-navy mt-3 uppercase leading-[0.95]">
            Free measure &amp; quotation.
            <br />
            <span className="text-moffat-red">No obligation.</span>
          </h2>
          <p className="mt-4 text-moffat-muted text-base">
            Fill the form and we&apos;ll be in touch — or message us straight on
            WhatsApp for the fastest reply.
          </p>
        </div>

        {/* Form */}
        <div>
          <div className="bg-white border border-moffat-line rounded-sm p-5 sm:p-7 md:p-8 shadow-sm">
            {success ? (
              <div data-testid="quote-success" className="text-center py-10 px-4 animate-float-up">
                <div className="mx-auto h-16 w-16 rounded-sm bg-moffat-whatsapp/15 text-moffat-whatsapp flex items-center justify-center">
                  <Check size={32} strokeWidth={3} />
                </div>
                <h3 className="mt-5 font-display text-3xl text-moffat-navy uppercase">
                  Almost there!
                </h3>
                <p className="mt-3 text-moffat-muted max-w-md mx-auto">
                  Your email app should have opened with your request ready to
                  send — just hit <strong>Send</strong> and we&apos;ll be in
                  touch shortly.
                </p>
                <p className="mt-2 text-sm text-moffat-muted max-w-md mx-auto">
                  Didn&apos;t open? Use WhatsApp instead:
                </p>
                <div className="mt-5 flex justify-center">
                  <WhatsAppButton variant="hero" testId="quote-success-whatsapp" />
                </div>
              </div>
            ) : (
              <form data-testid="quote-form" onSubmit={onSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Your name *">
                      <input
                        data-testid="quote-input-name"
                        required
                        value={form.name}
                        onChange={update("name")}
                        placeholder="John Smith"
                        className={inputCls}
                        autoComplete="name"
                      />
                    </Field>
                    <Field label="Phone number *">
                      <input
                        data-testid="quote-input-phone"
                        required
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="07…"
                        className={inputCls}
                        autoComplete="tel"
                      />
                    </Field>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Email (optional)">
                      <input
                        data-testid="quote-input-email"
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="you@example.com"
                        className={inputCls}
                        autoComplete="email"
                      />
                    </Field>
                    <Field label="Postcode (optional)">
                      <input
                        data-testid="quote-input-postcode"
                        value={form.postcode}
                        onChange={update("postcode")}
                        placeholder="DH1 …"
                        className={inputCls}
                        autoComplete="postal-code"
                      />
                    </Field>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Property type">
                      <select
                        data-testid="quote-input-property"
                        value={form.property_type}
                        onChange={update("property_type")}
                        className={inputCls}
                      >
                        <option value="">Select…</option>
                        {PROPERTY_OPTIONS.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Service interested in">
                      <select
                        data-testid="quote-input-service"
                        value={form.service}
                        onChange={update("service")}
                        className={inputCls}
                      >
                        <option value="">Select…</option>
                        {SERVICE_OPTIONS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <Field label="Tell us about the job (optional)">
                    <textarea
                      data-testid="quote-input-message"
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Rough room sizes, preferred styles, timescales…"
                      rows={4}
                      className={`${inputCls} resize-y`}
                    />
                  </Field>

                  <button
                    data-testid="quote-submit"
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 bg-moffat-red hover:bg-moffat-redDeep disabled:opacity-70 disabled:cursor-not-allowed text-white font-bold text-base px-6 py-4 rounded-sm shadow-md transition-all hover:-translate-y-0.5"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        OPENING EMAIL…
                      </>
                    ) : (
                      <>SEND VIA EMAIL</>
                    )}
                  </button>

                  <p className="text-xs text-moffat-muted text-center">
                    By submitting you agree to be contacted about your enquiry.
                    Prefer to talk?{" "}
                    <a
                      href={SITE.whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-bold text-moffat-navy hover:text-moffat-red"
                    >
                      Message on WhatsApp →
                    </a>
                  </p>
                </form>
              )}
            </div>
        </div>

        {/* Contact options under the form */}
        <div className="mt-10 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
            <a
              data-testid="contact-whatsapp"
              href={SITE.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Chat on WhatsApp"
              className="flex items-center justify-center bg-white border border-moffat-line hover:border-moffat-whatsapp rounded-sm p-3 transition-all hover:-translate-y-0.5 hover:shadow-md"
            >
              <img
                src={SITE.whatsappButtonImg}
                alt="Chat on WhatsApp"
                className="h-11 w-auto rounded-full"
              />
            </a>

            <a
              data-testid="contact-call"
              href={`tel:${SITE.phoneTel}`}
              className="flex items-center gap-4 bg-white border border-moffat-line hover:border-moffat-navy rounded-sm p-4 transition-all hover:-translate-y-0.5 hover:shadow-md group"
            >
              <span className="h-11 w-11 rounded-sm bg-moffat-navy text-white flex items-center justify-center flex-shrink-0">
                <Phone size={20} strokeWidth={2.5} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold uppercase tracking-wider text-moffat-muted">
                  Give us a
                </p>
                <p className="font-bold text-moffat-navy">Call us today</p>
              </div>
              <span className="text-xs font-bold text-moffat-navy group-hover:translate-x-1 transition-transform">
                →
              </span>
            </a>

            <a
              data-testid="contact-email"
              href={`mailto:${SITE.email}`}
              className="flex items-center gap-4 bg-white border border-moffat-line hover:border-moffat-red rounded-sm p-4 transition-all hover:-translate-y-0.5 hover:shadow-md group"
            >
              <span className="h-11 w-11 rounded-sm bg-moffat-red text-white flex items-center justify-center flex-shrink-0">
                <Mail size={20} strokeWidth={2.5} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold uppercase tracking-wider text-moffat-muted">
                  Send us
                </p>
                <p className="font-bold text-moffat-navy">An email</p>
              </div>
              <span className="text-xs font-bold text-moffat-red group-hover:translate-x-1 transition-transform">
                →
              </span>
            </a>
          </div>

          <div className="flex items-center justify-center gap-3 text-sm text-moffat-muted">
            <MapPin size={16} className="text-moffat-navy" strokeWidth={2.5} />
            <span>
              Covering{" "}
              <strong className="text-moffat-navy">{SITE.coverage}</strong>
            </span>
          </div>

          <div className="hidden md:flex items-center justify-center gap-5 bg-moffat-navy text-white border border-moffat-line rounded-sm p-5 max-w-xl mx-auto">
            <img
              src={SITE.qrUrl}
              alt="WhatsApp QR code"
              className="h-24 w-24 rounded-sm bg-white p-1 flex-shrink-0"
            />
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-moffat-red">
                Scan with your phone
              </p>
              <p className="font-display text-lg leading-tight mt-1">
                MESSAGE US ON WHATSAPP
              </p>
              <p className="text-xs text-white/70 mt-1">
                Quickest way to get a quote.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "block w-full rounded-sm border border-moffat-line bg-white px-3.5 py-3 text-base text-moffat-text placeholder-moffat-muted/60 shadow-sm focus:border-moffat-navy focus:ring-2 focus:ring-moffat-navy/15 focus:outline-none transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-wider text-moffat-muted mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
